<mat-toolbar color="primary" class="action-toolbar">
    <h3>Results</h3>
</mat-toolbar>

<div class="patient-list-container">

    <div class="filter-wrapper">
        <mat-form-field appearance="outline" class="filter-field">
            <mat-label>Search</mat-label>
            <input matInput [(ngModel)]="filterValue" (keyup)="applyFilter($event)" placeholder="Search patients"
                #input>
            <mat-icon matSuffix *ngIf="!filterValue">search</mat-icon>
            <mat-icon matSuffix *ngIf="filterValue" (click)="filterValue = ''; updateFilteredLists()"
                class="clickable">close</mat-icon>
            <mat-hint>Search by name, surname, patient number, diagnosis, or test names</mat-hint>
        </mat-form-field>

        <!--
            Role filter. Rendered ONLY when a loaded row actually names a role: no row carries
            `vendorDoctors` today, and an always-empty menu is worse than no menu.
            Interaction copied from patient-clinical-timeline: filter_list icon button ->
            mat-menu -> mat-checkbox multi-select, with stopPropagation on the content and on
            every item so the menu survives a second tick.
        -->
        <ng-container *ngIf="vendorDoctorRoleOptions.length">
            <button mat-icon-button class="role-filter-button" [matMenuTriggerFor]="roleFilterMenu"
                (click)="$event.stopPropagation()"
                [matTooltip]="selectedVendorDoctorRoles.length
                    ? 'Filtering by ' + selectedVendorDoctorRoles.length + ' role(s)'
                    : 'Filter by vendor doctor role'">
                <mat-icon>filter_list</mat-icon>
                <span class="role-filter-count" *ngIf="selectedVendorDoctorRoles.length">{{
                    selectedVendorDoctorRoles.length }}</span>
            </button>
            <mat-menu #roleFilterMenu="matMenu" class="filter-menu">
                <div mat-menu-content class="filter-menu-content" (click)="$event.stopPropagation()">
                    <div class="filter-options">
                        <h4>Vendor doctor role</h4>
                        <mat-checkbox *ngFor="let role of vendorDoctorRoleOptions"
                            [checked]="isVendorDoctorRoleSelected(role)"
                            (change)="toggleVendorDoctorRole(role, $event.checked)"
                            (click)="$event.stopPropagation()">
                            {{ role }}
                        </mat-checkbox>
                        <button mat-button type="button" class="role-filter-clear"
                            *ngIf="selectedVendorDoctorRoles.length"
                            (click)="clearVendorDoctorRoles(); $event.stopPropagation()">
                            Clear roles
                        </button>
                    </div>
                </div>
            </mat-menu>
        </ng-container>
    </div>

    <!--
        ONE definition of the badge row, used by all five tabs.
        `badges.length` is false for a row whose `vendorDoctors` is null (every pre-existing row),
        for a row whose only party is our own platform code, and for a row whose slots carry no
        role — and in each of those cases NOTHING is emitted: no container, no label, no space.
    -->
    <ng-template #vendorDoctorBadgeRow let-row>
        <ng-container *ngIf="vendorDoctorBadgesFor(row) as badges">
            <div class="vendor-doctor-badges" *ngIf="badges.length">
                <span class="vendor-doctor-badge" *ngFor="let badge of badges" [matTooltip]="badge.tooltip">
                    <span class="vendor-doctor-badge-label">{{ badge.label }}</span>
                    <span class="vendor-doctor-badge-role">{{ badge.role }}</span>
                </span>
            </div>
        </ng-container>
    </ng-template>

    <mat-tab-group (selectedIndexChange)="onTabChange($event)" [selectedIndex]="selectedTabIndex" class="tab-group"
        matTabGroupGesture [swipeLimitWidth]="80" [connectEdges]="true">
        <!-- Results Tab (Completed & Unseen) -->
        <mat-tab>
            <ng-template mat-tab-label>
                New Results ({{resultsCount || 0}})
            </ng-template>
            <div class="tab-content">
                <ng-container *ngIf="filteredResults.length; else noResults">
                    <mat-list>
                        <ng-container *ngFor="let group of filteredResults">
                            <ng-container *ngIf="group.items.length">
                                <div class="list-header">{{group.date | date: 'longDate'}}</div>
                                <ng-container *ngFor="let order of group.items; let last = last">
                                    <mat-list-item
                                        (click)="navigateToPatientFile(order.patientId || order.Patient?.id, order.id)"
                                        class="auto-height" [appPatientActive]="order.patientId || order.Patient?.id"
                                        [appPatientValidationStatus]="order.Patient"
                                        #valStatus="appPatientValidationStatus">
                                        <div class="lab-order-item">
                                            <div class="order-info">
                                                <app-patient-validation-messages
                                                    [valStatus]="valStatus"></app-patient-validation-messages>
                                                <div class="order-header">
                                                    <span class="order-number">#{{ order.orderNumber ||
                                                        order.id.substring(0, 8) }}</span>
                                                    <span class="patient-name-text">
                                                        <b>{{ (order.Patient?.surname || order.patientSnapshot?.surname)
                                                            }},
                                                            {{ (order.Patient?.name || order.patientSnapshot?.name)
                                                            }}</b>
                                                        <span class="patient-number"
                                                            *ngIf="order.Patient?.patientNumber || order.patientSnapshot?.patientNumber">
                                                            ({{ order.Patient?.patientNumber ||
                                                            order.patientSnapshot?.patientNumber }})
                                                        </span>
                                                    </span>
                                                    <span class="status-chip" [ngClass]="order.status">{{ order.status |
                                                        titlecase }}</span>
                                                    <span class="unseen-badge" *ngIf="!order.reviewedAt">New</span>
                                                </div>
                                                <div class="order-details">
                                                    {{ order.Patient?.diagnosis || order.patientSnapshot?.diagnosis }}
                                                </div>
                                                <div class="order-details" *ngIf="order.diagnosisString">
                                                    {{ order.diagnosisString }}
                                                </div>
                                                <div class="order-details" *ngIf="order.testNames">
                                                    {{ order.testNames }}
                                                </div>
                                                <ng-container
                                                    *ngTemplateOutlet="vendorDoctorBadgeRow; context: { $implicit: order }"></ng-container>
                                                <div class="order-meta" *ngIf="order.providerLogo">
                                                    <div class="provider-info">
                                                        <img [src]="'data:image/png;base64,' + order.providerLogo"
                                                            alt="{{ order.labProvider }} logo" class="provider-logo">
                                                    </div>
                                                </div>
                                                <div class="order-meta" *ngIf="order.resultDate">
                                                    Completed: {{ order.resultDate | date: 'yyyy-MM-dd HH:mm' }}
                                                </div>
                                                <div class="order-meta">
                                                    Created By: {{ order.createdBy || 'Unknown' }} at {{
                                                    (order.createdAt ||
                                                    order.orderDate) | date: 'yyyy-MM-dd HH:mm' }}
                                                </div>
                                            </div>
                                            <div class="view-icon" (click)="onViewIconClick($event, order)">
                                                <mat-icon>visibility</mat-icon>
                                            </div>
                                        </div>
                                    </mat-list-item>
                                    <mat-divider *ngIf="!last"></mat-divider>
                                </ng-container>
                            </ng-container>
                        </ng-container>
                        <div class="mobile-spacer"></div>
                    </mat-list>
                </ng-container>
                <ng-template #noResults>
                    <p class="no-records-message">No results found.</p>
                </ng-template>
            </div>
        </mat-tab>

        <!-- Unlinked Tab (results Lancet sent for orders never placed through MedicAI) -->
        <mat-tab>
            <ng-template mat-tab-label>
                Unlinked ({{unlinkedCount || 0}})
            </ng-template>
            <div class="tab-content">
                <ng-container *ngIf="filteredUnlinked.length; else noUnlinked">
                    <mat-list>
                        <ng-container *ngFor="let group of filteredUnlinked">
                            <ng-container *ngIf="group.items.length">
                                <div class="list-header">{{ group.date ? (group.date | date: 'longDate') : 'Unknown date' }}</div>
                                <ng-container *ngFor="let row of group.items; let last = last">
                                    <mat-list-item class="auto-height">
                                        <div class="lab-order-item unlinked-item">
                                            <div class="order-info">
                                                <div class="order-header">
                                                    <span class="order-number">#{{ row.specimenNumber || row.id.substring(0, 8) }}</span>
                                                    <span class="patient-name-text">
                                                        <b>{{ unlinkedPatientName(row) }}</b>
                                                        <span class="patient-number" *ngIf="row.demographics?.idNumber">
                                                            (ID {{ row.demographics?.idNumber }})
                                                        </span>
                                                        <span class="patient-number"
                                                            *ngIf="!row.demographics?.idNumber && row.demographics?.passportNumber">
                                                            (Passport {{ row.demographics?.passportNumber }})
                                                        </span>
                                                    </span>
                                                    <span class="status-chip unlinked">Unlinked</span>
                                                    <span class="status-chip" *ngIf="row.reportType">{{ row.reportType }}</span>
                                                </div>
                                                <div class="order-details">
                                                    Ordering doctor: {{ unlinkedDoctor(row) }}
                                                </div>
                                                <div class="order-details" *ngIf="row.demographics?.dateOfBirth || row.demographics?.sex">
                                                    <span *ngIf="row.demographics?.dateOfBirth">DOB {{ row.demographics?.dateOfBirth }}</span>
                                                    <span *ngIf="row.demographics?.dateOfBirth && row.demographics?.sex"> &middot; </span>
                                                    <span *ngIf="row.demographics?.sex">{{ row.demographics?.sex }}</span>
                                                </div>
                                                <ng-container
                                                    *ngTemplateOutlet="vendorDoctorBadgeRow; context: { $implicit: row }"></ng-container>
                                                <div class="order-meta">
                                                    {{ row.vendor || 'Lab' }} &middot; received {{ row.receivedAt | date: 'yyyy-MM-dd HH:mm' }}
                                                    <span *ngIf="row.receiveCount && row.receiveCount > 1"> &middot; sent {{ row.receiveCount }}&times;</span>
                                                </div>
                                            </div>
                                            <div class="unlinked-actions">
                                                <button type="button" mat-stroked-button color="primary"
                                                    [disabled]="isUnlinkedBusy(row)"
                                                    (click)="linkUnlinkedResult($event, row)">Link</button>
                                                <button type="button" mat-button
                                                    [disabled]="isUnlinkedBusy(row)"
                                                    (click)="dismissUnlinkedResult($event, row)">Dismiss</button>
                                                <button type="button" mat-icon-button
                                                    matTooltip="View results (read only)"
                                                    [disabled]="isUnlinkedOpening(row)"
                                                    (click)="viewUnlinkedResults($event, row)">
                                                    <mat-icon>visibility</mat-icon>
                                                </button>
                                                <button type="button" mat-icon-button
                                                    matTooltip="Download Lab Results as PDF"
                                                    [disabled]="isUnlinkedDownloading(row)"
                                                    (click)="downloadUnlinkedResultPdf($event, row)">
                                                    <mat-icon>download</mat-icon>
                                                </button>
                                                <button type="button" mat-icon-button
                                                    matTooltip="View raw vendor payload"
                                                    (click)="viewUnlinkedPayload($event, row)">
                                                    <mat-icon>data_object</mat-icon>
                                                </button>
                                            </div>
                                        </div>
                                    </mat-list-item>
                                    <mat-divider *ngIf="!last"></mat-divider>
                                </ng-container>
                            </ng-container>
                        </ng-container>
                        <div class="mobile-spacer"></div>
                    </mat-list>
                </ng-container>
                <ng-template #noUnlinked>
                    <p class="no-records-message">No unlinked results.</p>
                </ng-template>
            </div>
        </mat-tab>

        <!-- Flagged Tab -->
        <mat-tab>
            <ng-template mat-tab-label>
                Flagged Results ({{flaggedCount || 0}})
            </ng-template>
            <div class="tab-content">
                <ng-container *ngIf="filteredFlagged.length; else noFlagged">
                    <mat-list>
                        <ng-container *ngFor="let group of filteredFlagged">
                            <ng-container *ngIf="group.items.length">
                                <div class="list-header">{{group.date | date: 'longDate'}}</div>
                                <ng-container *ngFor="let order of group.items; let last = last">
                                    <mat-list-item
                                        (click)="navigateToPatientFile(order.patientId || order.Patient?.id, order.id)"
                                        class="auto-height" [appPatientActive]="order.patientId || order.Patient?.id"
                                        [appPatientValidationStatus]="order.Patient"
                                        #valStatus="appPatientValidationStatus">
                                        <div class="lab-order-item">
                                            <div class="order-info">
                                                <app-patient-validation-messages
                                                    [valStatus]="valStatus"></app-patient-validation-messages>
                                                <div class="order-header">
                                                    <mat-icon class="flag-icon">flag</mat-icon>
                                                    <span class="order-number">#{{ order.orderNumber ||
                                                        order.id.substring(0, 8) }}</span>
                                                    <span class="patient-name-text">
                                                        <b>{{ (order.Patient?.surname || order.patientSnapshot?.surname)
                                                            }},
                                                            {{ (order.Patient?.name || order.patientSnapshot?.name)
                                                            }}</b>
                                                        <span class="patient-number"
                                                            *ngIf="order.Patient?.patientNumber || order.patientSnapshot?.patientNumber">
                                                            ({{ order.Patient?.patientNumber ||
                                                            order.patientSnapshot?.patientNumber }})
                                                        </span>
                                                    </span>
                                                    <span class="status-chip" [ngClass]="order.status">{{ order.status |
                                                        titlecase }}</span>
                                                    <span class="flagged-badge">Flagged</span>
                                                </div>
                                                <div class="order-details">
                                                    {{ order.Patient?.diagnosis || order.patientSnapshot?.diagnosis }}
                                                </div>
                                                <div class="order-details" *ngIf="order.diagnosisString">
                                                    {{ order.diagnosisString }}
                                                </div>
                                                <div class="order-details" *ngIf="order.testNames">
                                                    {{ order.testNames }}
                                                </div>
                                                <ng-container
                                                    *ngTemplateOutlet="vendorDoctorBadgeRow; context: { $implicit: order }"></ng-container>
                                                <div class="order-meta" *ngIf="order.providerLogo">
                                                    <div class="provider-info">
                                                        <img [src]="'data:image/png;base64,' + order.providerLogo"
                                                            alt="{{ order.labProvider }} logo" class="provider-logo">
                                                    </div>
                                                </div>
                                                <div class="order-meta" *ngIf="order.resultDate">
                                                    Completed: {{ order.resultDate | date: 'yyyy-MM-dd HH:mm' }}
                                                </div>
                                                <div class="order-meta">
                                                    Created By: {{ order.createdBy || 'Unknown' }} at {{
                                                    (order.createdAt ||
                                                    order.orderDate) | date: 'yyyy-MM-dd HH:mm' }}
                                                </div>
                                            </div>
                                            <div class="view-icon" (click)="onViewIconClick($event, order)">
                                                <mat-icon>visibility</mat-icon>
                                            </div>
                                        </div>
                                    </mat-list-item>
                                    <mat-divider *ngIf="!last"></mat-divider>
                                </ng-container>
                            </ng-container>
                        </ng-container>
                        <div class="mobile-spacer"></div>
                    </mat-list>
                </ng-container>
                <ng-template #noFlagged>
                    <p class="no-records-message">No flagged results found.</p>
                </ng-template>
            </div>
        </mat-tab>


        <!-- Failed Tab -->
        <mat-tab>
            <ng-template mat-tab-label>
                Failed Orders({{failedCount || 0}})
            </ng-template>
            <div class="tab-content">
                <ng-container *ngIf="filteredFailed.length; else noFailed">
                    <mat-list>
                        <ng-container *ngFor="let group of filteredFailed">
                            <ng-container *ngIf="group.items.length">
                                <div class="list-header">{{group.date | date: 'longDate'}}</div>
                                <ng-container *ngFor="let order of group.items; let last = last">
                                    <mat-list-item
                                        (click)="navigateToPatientFile(order.patientId || order.Patient?.id, order.id)"
                                        class="auto-height" [appPatientActive]="order.patientId || order.Patient?.id"
                                        [appPatientValidationStatus]="order.Patient"
                                        #valStatus="appPatientValidationStatus">
                                        <div class="lab-order-item">
                                            <div class="order-info">
                                                <app-patient-validation-messages
                                                    [valStatus]="valStatus"></app-patient-validation-messages>
                                                <div class="order-header">
                                                    <span class="order-number">#{{ order.orderNumber ||
                                                        order.id.substring(0, 8) }}</span>
                                                    <span class="patient-name-text">
                                                        <b>{{ (order.Patient?.surname || order.patientSnapshot?.surname)
                                                            }},
                                                            {{ (order.Patient?.name || order.patientSnapshot?.name)
                                                            }}</b>
                                                        <span class="patient-number"
                                                            *ngIf="order.Patient?.patientNumber || order.patientSnapshot?.patientNumber">
                                                            ({{ order.Patient?.patientNumber ||
                                                            order.patientSnapshot?.patientNumber }})
                                                        </span>
                                                    </span>
                                                    <span class="status-chip" [ngClass]="order.status">{{ order.status |
                                                        titlecase }}</span>
                                                </div>
                                                <div class="order-details">
                                                    {{ order.Patient?.diagnosis || order.patientSnapshot?.diagnosis }}
                                                </div>
                                                <div class="order-details" *ngIf="order.diagnosisString">
                                                    {{ order.diagnosisString }}
                                                </div>
                                                <div class="order-details" *ngIf="order.testNames">
                                                    {{ order.testNames }}
                                                </div>
                                                <ng-container
                                                    *ngTemplateOutlet="vendorDoctorBadgeRow; context: { $implicit: order }"></ng-container>
                                                <div class="order-meta error-message" *ngIf="order.errorMessage">
                                                    <mat-icon class="error-icon">error</mat-icon>
                                                    Error: {{ order.errorMessage }}
                                                </div>
                                                <div class="order-meta" *ngIf="order.providerLogo">
                                                    <div class="provider-info">
                                                        <img [src]="'data:image/png;base64,' + order.providerLogo"
                                                            alt="{{ order.labProvider }} logo" class="provider-logo">
                                                    </div>
                                                </div>
                                                <div class="order-meta">
                                                    Created By: {{ order.createdBy || 'Unknown' }} at {{
                                                    (order.createdAt ||
                                                    order.orderDate) | date: 'yyyy-MM-dd HH:mm' }}
                                                </div>
                                            </div>
                                            <div class="edit-icon" (click)="editLabOrder(order, $event)"
                                                *ngIf="!stateUnit && (order.status === 'failed' || ['pending', 'ordered', 'in_progress'].includes(order.status))">
                                                <mat-icon>edit</mat-icon>
                                            </div>
                                        </div>
                                    </mat-list-item>
                                    <mat-divider *ngIf="!last"></mat-divider>
                                </ng-container>
                            </ng-container>
                        </ng-container>
                        <div class="mobile-spacer"></div>
                    </mat-list>
                </ng-container>
                <ng-template #noFailed>
                    <p class="no-records-message">No failed orders found.</p>
                </ng-template>
            </div>
        </mat-tab>



        <!-- Pending Tab -->
        <mat-tab>
            <ng-template mat-tab-label>
                Pending Orders ({{pendingCount || 0}})
            </ng-template>
            <div class="tab-content">
                <ng-container *ngIf="filteredPending.length; else noPending">
                    <mat-list>
                        <ng-container *ngFor="let group of filteredPending">
                            <ng-container *ngIf="group.items.length">
                                <div class="list-header">{{group.date | date: 'longDate'}}</div>
                                <ng-container *ngFor="let order of group.items; let last = last">
                                    <mat-list-item
                                        (click)="navigateToPatientFile(order.patientId || order.Patient?.id, order.id)"
                                        class="auto-height" [appPatientActive]="order.patientId || order.Patient?.id"
                                        [appPatientValidationStatus]="order.Patient"
                                        #valStatus="appPatientValidationStatus">
                                        <div class="lab-order-item">
                                            <div class="order-info">
                                                <app-patient-validation-messages
                                                    [valStatus]="valStatus"></app-patient-validation-messages>
                                                <div class="order-header">
                                                    <span class="order-number">#{{ order.orderNumber ||
                                                        order.id.substring(0, 8) }}</span>
                                                    <span class="patient-name-text">
                                                        <b>{{ (order.Patient?.surname || order.patientSnapshot?.surname)
                                                            }},
                                                            {{ (order.Patient?.name || order.patientSnapshot?.name)
                                                            }}</b>
                                                        <span class="patient-number"
                                                            *ngIf="order.Patient?.patientNumber || order.patientSnapshot?.patientNumber">
                                                            ({{ order.Patient?.patientNumber ||
                                                            order.patientSnapshot?.patientNumber }})
                                                        </span>
                                                    </span>
                                                    <span class="status-chip" [ngClass]="order.status">{{ order.status |
                                                        titlecase }}</span>
                                                </div>
                                                <div class="order-details">
                                                    {{ order.Patient?.diagnosis || order.patientSnapshot?.diagnosis }}
                                                </div>
                                                <div class="order-details" *ngIf="order.diagnosisString">
                                                    {{ order.diagnosisString }}
                                                </div>
                                                <div class="order-details" *ngIf="order.testNames">
                                                    {{ order.testNames }}
                                                </div>
                                                <ng-container
                                                    *ngTemplateOutlet="vendorDoctorBadgeRow; context: { $implicit: order }"></ng-container>
                                                <div class="order-meta" *ngIf="order.providerLogo">
                                                    <div class="provider-info">
                                                        <img [src]="'data:image/png;base64,' + order.providerLogo"
                                                            alt="{{ order.labProvider }} logo" class="provider-logo">
                                                    </div>
                                                </div>
                                                <div class="order-meta">
                                                    Created By: {{ order.createdBy || 'Unknown' }} at {{
                                                    (order.createdAt ||
                                                    order.orderDate) | date: 'yyyy-MM-dd HH:mm' }}
                                                </div>
                                            </div>
                                            <div class="edit-icon" (click)="editLabOrder(order, $event)"
                                                *ngIf="!stateUnit && (order.status === 'failed' || ['pending', 'ordered', 'in_progress'].includes(order.status))">
                                                <mat-icon>edit</mat-icon>
                                            </div>
                                        </div>
                                    </mat-list-item>
                                    <mat-divider *ngIf="!last"></mat-divider>
                                </ng-container>
                            </ng-container>
                        </ng-container>
                        <div class="mobile-spacer"></div>
                    </mat-list>
                </ng-container>
                <ng-template #noPending>
                    <p class="no-records-message">No pending orders found.</p>
                </ng-template>
            </div>
        </mat-tab>
    </mat-tab-group>

</div>